import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Uuid } from '@ftr/contracts/type/shared/Uuid'
import { ComposableCollapseComponent } from '@ftr/foundation'
import { BehaviorSubject, Observable, combineLatest, debounceTime, distinctUntilChanged, map } from 'rxjs'
import { BasicCheckboxComponent } from '../basic-checkbox'
import { TristateValue } from './checkbox-item'
import { CheckboxTreeItem } from './checkbox-tree-item'

/**
 * Typing debounce. Essentially waits for the user to stop typing before updating the UI.
 */
const FILTER_DEBOUNCE = 100

export interface CollapsibleCheckboxListViewModel {
  filteredItems: CheckboxTreeItem[]
  hasFilter: boolean
  totalFilterCount: number
}

@Component({
  selector: 'ftr-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  styleUrls: ['./checkbox-tree.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BasicCheckboxComponent, ComposableCollapseComponent, AsyncPipe],
})
export class CheckboxTreeComponent implements OnInit {
  @Input() items: Observable<CheckboxTreeItem[]>
  @Input() enableSelectAll: boolean = true
  @Input() enableSelectFiltered: boolean = true

  // Allows us to display "Select all filtered xyzs" where xyzs might be "locations", "users", "gremlins" etc
  @Input() pluralNoun: string = 'items'
  @Input() maxSelectedItems: number | undefined

  @Output() childValueChange = new EventEmitter<readonly [Uuid, boolean]>()
  @Output() totalFilterCount = new EventEmitter<number>()

  filter = new BehaviorSubject<string>('')

  viewModel: Observable<CollapsibleCheckboxListViewModel>
  private selectedChildrenIds = new Set<string>()

  ngOnInit(): void {
    this.selectedChildrenIds = new Set()
    this.viewModel = combineLatest([this.items, this.getDebouncedFilter()]).pipe(
      map(([items, filter]) => {
        const hasFilter = filter !== ''
        const filteredItems = hasFilter ? items.map(i => i.withFilter(filter)) : items
        const totalFilterCount = filteredItems.reduce((acc, item) => acc + item.shownChildrenCount, 0)
        filteredItems
          .reduce((acc, item) => acc.concat(item.selectedChildrenIds), [] as string[])
          .forEach(id => this.selectedChildrenIds.add(id))
        this.totalFilterCount.emit(totalFilterCount)
        return {
          hasFilter,
          filteredItems,
          totalFilterCount,
        }
      }),
    )
  }

  private getDebouncedFilter(): Observable<string> {
    return this.filter.pipe(
      debounceTime(FILTER_DEBOUNCE),
      map(filter => filter.toLowerCase().trim()),
      distinctUntilChanged(),
    )
  }

  trackById(_: number, thing: { id: string }): string {
    return thing.id
  }

  setParentSelected(parent: CheckboxTreeItem, value: boolean): void {
    parent.items.forEach(child => this.onSelectionChange(value, child.id))
  }

  setFilteredParentSelected(parent: CheckboxTreeItem, value: boolean): void {
    parent.getShowChildren().forEach(child => this.onSelectionChange(value, child.id))
  }

  onSelectionChange(value: boolean, id: Uuid): void {
    if (value) {
      if (!this.maxSelectedItems || this.selectedChildrenIds.size < this.maxSelectedItems) {
        this.selectedChildrenIds.add(id)
        this.childValueChange.emit([id, value])
      }
    } else {
      this.selectedChildrenIds.delete(id)
      this.childValueChange.emit([id, value])
    }
  }

  getSelectAllValue(items: CheckboxTreeItem[]): TristateValue {
    const values = items.map(x => x.value)
    return this.combineTristateValues(values)
  }

  getSelectFilteredValue(items: CheckboxTreeItem[]): TristateValue {
    const filteredItems = items.filter(x => !x.hidden)
    const selectedFilteredItems = filteredItems.map(x => x.filterValue)
    return this.combineTristateValues(selectedFilteredItems)
  }

  selectAll(items: CheckboxTreeItem[], value: boolean): void {
    items.flatMap(x => x.items).forEach(item => this.onSelectionChange(value, item.id))
  }

  selectAllFiltered(items: CheckboxTreeItem[], value: boolean): void {
    items.flatMap(x => x.getShowChildren()).forEach(item => this.onSelectionChange(value, item.id))
  }

  private combineTristateValues(values: TristateValue[]): TristateValue {
    if (values.every(v => v === false)) {
      return false
    } else if (values.every(v => v === true)) {
      return true
    }
    return 'indeterminate'
  }
}
